import { createContext, useContext, type ReactNode } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { SessionUser } from "@/platform/http";
import { api } from "@/platform/client/api.js";

interface AuthState {
  authenticated: boolean;
  loading: boolean;
  login: (password: string) => Promise<void>;
  logout: () => Promise<void>;
}

const AuthContext = createContext<AuthState | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const qc = useQueryClient();
  const me = useQuery({
    queryKey: ["me"],
    queryFn: () => api.get<SessionUser>("/auth/me"),
    staleTime: 30_000,
  });

  const loginMut = useMutation({
    mutationFn: (password: string) => api.post<SessionUser>("/auth/login", { password }),
    onSuccess: () => qc.invalidateQueries(),
  });

  const logoutMut = useMutation({
    mutationFn: () => api.post("/auth/logout"),
    onSuccess: () => {
      qc.clear();
      qc.invalidateQueries({ queryKey: ["me"] });
    },
  });

  const value: AuthState = {
    authenticated: me.data?.authenticated ?? false,
    loading: me.isLoading,
    login: async (password) => {
      await loginMut.mutateAsync(password);
    },
    logout: async () => {
      await logoutMut.mutateAsync();
    },
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthState {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
}
